import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyRow } from '@/features/EmptyRow';
import { Icon } from '@/kit/Icon';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  formatDisplayDate,
  type ShareCapitalDialogData,
  type ShareCapitalRow,
} from '@/core/models/report-options.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { AddshareCapitalDialog } from './AddshareCapitalDialog';
import { ReportSettingsFrame, SETTINGS_ROUTE, formatDecimal, useReportClient } from './common';
import './ShareCapital.scss';

/** Report Settings > Share Capital: the share capital lines, plus whether the note shows the authorised shares. */
export function ShareCapital() {
  const navigate = useNavigate();
  const client = useReportClient();
  const [dataSource, setDataSource] = useState<ShareCapitalRow[]>([]);
  /** The list has been read, so an empty one can say so (never while it is still loading). */
  const [loaded, setLoaded] = useState(false);
  const [displayAuthorisedShares, setDisplayAuthorisedShares] = useState(true);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = () => {
    if (!client) return;
    reportOptionsService.getShareCapital(client.id).then(
      (view) => {
        if (!alive.current) return;
        setDataSource(view.shares);
        setLoaded(true);
        setDisplayAuthorisedShares(view.displayAuthorisedShares);
      },
      (error) => alive.current && notifyError(error),
    );
  };

  useEffect(() => {
    load();
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDisplayAuthorisedChange = (checked: boolean) => {
    if (!client) return;
    const previous = displayAuthorisedShares;
    setDisplayAuthorisedShares(checked);
    reportOptionsService.setDisplayAuthorisedShares(client.id, checked).then(
      // 2 Nov design review: a toggle saves at once and says so, as Company Contacts' options already do.
      () => alive.current && notifySuccess(NotificationTextMessage.successMessage),
      (error) => {
        if (!alive.current) return;
        setDisplayAuthorisedShares(previous);
        notifyError(error);
      },
    );
  };

  const openShareDialog = (share?: ShareCapitalRow) => {
    if (!client) return;
    const data: ShareCapitalDialogData = { clientId: client.id, share };
    openDialog<ShareCapitalDialogData, ShareCapitalRow>(AddshareCapitalDialog, {
      disableClose: true,
      panelClass: 'cst-invite',
      data,
    })
      .afterClosed()
      .then((saved) => {
        if (saved) {
          notifySuccess(NotificationTextMessage.successMessage);
          load();
        }
      });
  };

  const onDeleteShare = (row: ShareCapitalRow) => {
    openDialog<{ itemName: string; count: number }, boolean>(ClosePopup, {
      data: { itemName: 'Share Capital', count: 1 },
    })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) return;
        reportOptionsService.deleteShareCapital(row.id).then(
          () => {
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  const nowrap = (value: string | number | null | undefined) => <div className="white-space-nowrap">{value}</div>;

  const columns: DataTableColumn<ShareCapitalRow>[] = [
    {
      id: 'sharetype',
      header: 'Share Type',
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.shareTypeName}</div>
        </div>
      ),
    },
    {
      id: 'issuedate',
      header: 'Issue Date',
      headerClassName: 'w-100',
      cell: (e) => nowrap(formatDisplayDate(e.issueDate)),
    },
    { id: 'noofshare', header: 'No of Shares', headerClassName: 'w-100', cell: (e) => nowrap(e.noOfShares) },
    {
      id: 'backdate',
      header: 'Buy Back Date',
      headerClassName: 'w-100',
      cell: (e) => nowrap(formatDisplayDate(e.buyBackDate)),
    },
    { id: 'shares', header: 'Buy Back Shares', headerClassName: 'w-120', cell: (e) => nowrap(e.buyBackShares) },
    {
      id: 'shareclass',
      header: 'Share Class',
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.shareClassLabel}</div>
        </div>
      ),
    },
    {
      id: 'sharevalue',
      header: 'Share Value',
      headerClassName: 'w-100',
      cell: (e) => nowrap(formatDecimal(e.shareValue, 2, 4)),
    },
    { id: 'authshare', header: 'Authorised Share', headerClassName: 'w-100', cell: (e) => nowrap(e.authorisedShares) },
    {
      id: 'allotshare',
      header: 'Alloted Shares',
      headerClassName: '',
      cell: (element) => <span className="white-space-nowrap">{element.allottedShares}</span>,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Edit" onClick={() => openShareDialog(element)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => onDeleteShare(element)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <ReportSettingsFrame
      title="Share Capital"
      hostClassName="share-capital-host"
      onClose={() => navigate(SETTINGS_ROUTE)}
    >
      <div className="sidenav-body">
        <div className="form-field-title pt-1 pb-1 d-flex align-items-center justify-space-between">
          <Toggle checked={displayAuthorisedShares} onCheckedChange={onDisplayAuthorisedChange}>
            Display authorised shares in Share Capital note.
          </Toggle>
          <Button className="action-button primary-button border-radius-6" onClick={() => openShareDialog()}>
            <Icon name="add" />
            <span>Add New Share</span>
          </Button>
        </div>
        <div>
          <div className="mat-table-wrapper">
            <DataTable
              columns={columns}
              rows={dataSource}
              rowKey={(row) => row.id}
              stickyHeader
              noDataMessage={
                loaded ? <EmptyRow icon="pie_chart" title="No shares added yet" hint="Use Add New Share to add the first share class." /> : undefined
              }
            />
          </div>
        </div>
      </div>
    </ReportSettingsFrame>
  );
}
